import { ArrowBack, ArrowForward, ChevronRight } from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Divider,
  Grid,
  Link,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  CTABanner,
  PartnerBadge,
  PartnershipInquiryModal,
  RelationshipTile,
} from "components/partners";
import Layout from "components/Layout";
import {
  useGetPartnerQuery,
  useGetRelatedPartnersQuery,
  useGetPartnersQuery,
} from "features/partners/partnersApi";
import type { RelationshipTileKey } from "types/partners";
import { CALIBRATE_FONT_FAMILY_ACCENT } from "components/hoc/Theme";

export default function PartnerDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isInquiryModalOpen, setIsInquiryModalOpen] = useState(false);
  const theme = useTheme();

  const { data: partner, isLoading, error } = useGetPartnerQuery(slug || "");
  const { data: relatedPartners } = useGetRelatedPartnersQuery(slug || "");
  const { data: allPartnersData } = useGetPartnersQuery({
    page_size: 1000,
  });
  const allPartners = allPartnersData?.results || [];

  const handleBackToDirectory = () => {
    const params = new URLSearchParams();
    if (searchParams.get("search"))
      params.set("search", searchParams.get("search")!);
    if (searchParams.get("partner_type"))
      params.set("partner_type", searchParams.get("partner_type")!);
    if (searchParams.get("industry_type"))
      params.set("industry_type", searchParams.get("industry_type")!);
    if (searchParams.get("sort")) params.set("sort", searchParams.get("sort")!);
    const queryString = params.toString();
    navigate(`/partners${queryString ? `?${queryString}` : ""}`);
  };

  if (isLoading) {
    return (
      <Layout title="Partner Details" content="Partner Details">
        <Container
          maxWidth="xl"
          sx={{
            py: 4,
            height: "calc(100vh - 200px)",
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            flexGrow={1}
          >
            <CircularProgress />
          </Box>
        </Container>
      </Layout>
    );
  }

  if (error || !partner) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Typography textAlign="center" color="error">
          Partner not found.
        </Typography>
        <Box textAlign="center" mt={2}>
          <Button onClick={() => navigate("/partners")}>
            Back to Partner Directory
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Layout title="Partner Details" content="Partner Details">
      <Box
        sx={{
          backgroundColor: "#F3F4F5",
          py: 4,
        }}
      >
        <Box
          sx={{
            px: 3,
          }}
        >
          <Link
            component="button"
            onClick={handleBackToDirectory}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              color: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.9)"
                  : "rgba(31,41,51,0.9)",
              fontSize: {
                xs: "0.875rem",
                sm: "0.9375rem",
                md: "1rem",
                lg: "1.0625rem",
                xl: "1.125rem",
              },
              textDecoration: "none",
              mb: 3,
              cursor: "pointer",
              background: "none",
              border: "none",
              padding: 0,
              fontFamily: CALIBRATE_FONT_FAMILY_ACCENT,
              fontWeight: 400,
              "&:hover": {
                textDecoration: "underline",
              },
            }}
          >
            <ArrowBack sx={{ fontSize: { xs: 16, md: 18, lg: 20, xl: 22 } }} />
            Back to Partner Directory
          </Link>

          <Grid container spacing={2}>
            <Grid item xs={12} md={9}>
              <Card elevation={1} sx={{ borderRadius: "8px" }}>
                <CardContent sx={{ p: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      mb: 2,
                      flexWrap: "wrap",
                      gap: 2,
                    }}
                  >
                    <Box>
                      <Typography
                        variant="h3"
                        sx={{
                          fontFamily: CALIBRATE_FONT_FAMILY_ACCENT,
                          fontSize: {
                            xs: "1.75rem",
                            sm: "2rem",
                            md: "2.25rem",
                            lg: "2.5rem",
                            xl: "2.75rem",
                          },
                          fontWeight: 700,
                          color: (theme) =>
                            theme.palette.mode === "dark"
                              ? "rgba(255,255,255,0.9)"
                              : "rgba(31,41,51,0.9)",
                          mb: 1,
                        }}
                      >
                        {partner.name}
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          gap: { xs: 1, sm: 1, md: 1.5, lg: 1.5, xl: 2 },
                          flexWrap: "wrap",
                          alignItems: "center",
                        }}
                      >
                        <Box sx={{ display: "inline-flex" }}>
                          <PartnerBadge
                            type="industry"
                            value={partner.industry_type}
                            label={partner.industry_type_display}
                            size="medium"
                          />
                        </Box>
                        <Box sx={{ display: "inline-flex" }}>
                          <PartnerBadge
                            type="partner"
                            value={partner.partner_type}
                            label={partner.partner_type_display}
                            size="medium"
                          />
                        </Box>
                      </Box>
                    </Box>

                    {partner.logo_url && (
                      <Box
                        component="img"
                        src={partner.logo_url}
                        alt={partner.name}
                        sx={{
                          maxWidth: {
                            xs: 160,
                            sm: 180,
                            md: 200,
                            lg: 220,
                            xl: 240,
                          },
                          maxHeight: { xs: 64, sm: 72, md: 80, lg: 88, xl: 96 },
                          minHeight: { xs: 40, sm: 48, md: 56, lg: 64, xl: 72 },
                          objectFit: "contain",
                        }}
                      />
                    )}
                  </Box>

                  <Divider sx={{ my: 3 }} />

                  <Box sx={{ mb: 4 }}>
                    <Typography
                      variant="h5"
                      sx={{
                        fontFamily: "Montserrat, sans-serif",
                        fontSize: {
                          xs: "1rem",
                          sm: "1.125rem",
                          md: "1.25rem",
                          lg: "1.3125rem",
                          xl: "1.375rem",
                        },
                        fontWeight: 400,
                        mb: 1,
                        color: "text.primary",
                      }}
                    >
                      OUR PARTNERSHIP
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: CALIBRATE_FONT_FAMILY_ACCENT,
                        fontSize: {
                          xs: "0.6875rem",
                          sm: "0.71875rem",
                          md: "0.75rem",
                          lg: "0.8125rem",
                          xl: "0.875rem",
                        },
                        color: "text.secondary",
                        mb: 3,
                      }}
                    >
                      {partner.partnership_description}
                    </Typography>

                    {partner.relationship_tiles &&
                      partner.relationship_tiles.length > 0 && (
                        <Grid container spacing={2}>
                          {partner.relationship_tiles.map(
                            (tile: RelationshipTileKey) => (
                              <Grid item xs={12} sm={6} key={tile}>
                                <RelationshipTile tileKey={tile} />
                              </Grid>
                            ),
                          )}
                        </Grid>
                      )}
                  </Box>

                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="h5"
                      sx={{
                        fontFamily: "Montserrat, sans-serif",
                        fontSize: {
                          xs: "1rem",
                          sm: "1.125rem",
                          md: "1.25rem",
                          lg: "1.3125rem",
                          xl: "1.375rem",
                        },
                        fontWeight: 400,
                        mb: 1,
                        color: "text.primary",
                      }}
                    >
                      ABOUT THIS PARTNER
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: CALIBRATE_FONT_FAMILY_ACCENT,
                        fontSize: {
                          xs: "0.6875rem",
                          sm: "0.71875rem",
                          md: "0.75rem",
                          lg: "0.8125rem",
                          xl: "0.875rem",
                        },
                        color: "text.secondary",
                        lineHeight: 1.6,
                      }}
                    >
                      {partner.details_description}
                    </Typography>
                  </Box>

                  <Link
                    component="button"
                    onClick={() => setIsInquiryModalOpen(true)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      fontFamily: CALIBRATE_FONT_FAMILY_ACCENT,
                      fontSize: {
                        xs: "0.625rem",
                        sm: "0.6875rem",
                        md: "0.75rem",
                        lg: "0.8125rem",
                        xl: "0.875rem",
                      },
                      fontWeight: 700,
                      color: (theme) =>
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.9)"
                          : "rgba(31,41,51,0.9)",
                      textDecoration: "none",
                      cursor: "pointer",
                      background: "none",
                      border: "none",
                      padding: 0,
                      "&:hover": {
                        textDecoration: "underline",
                      },
                    }}
                  >
                    Learn more about this partnership
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <ArrowForward sx={{ fontSize: 14 }} />
                    </Box>
                  </Link>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Card elevation={1} sx={{ borderRadius: "8px" }}>
                  <CardContent sx={{ p: 2.5, textAlign: "center" }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontFamily: "Montserrat, sans-serif",
                        fontSize: "1.125rem",
                        fontWeight: 400,
                        mb: 1.5,
                        color: "text.primary",
                      }}
                    >
                      INTERESTED IN THIS PARTNER?
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: "Montserrat, sans-serif",
                        fontSize: "0.875rem",
                        color: "text.secondary",
                        mb: 2,
                      }}
                    >
                      Connect with the CalibrateHCM partnerships team to learn
                      more about how this partner could benefit your
                      organization.
                    </Typography>
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={() => setIsInquiryModalOpen(true)}
                      sx={{
                        fontFamily: CALIBRATE_FONT_FAMILY_ACCENT,
                        fontSize: "0.8125rem",
                        backgroundColor: "#31A0B1",
                        color: "#FFFFFF",
                        fontWeight: 500,
                        textTransform: "uppercase",
                        px: 2,
                        py: 1,
                        borderRadius: "6px",
                        boxShadow: 2,
                        "&:hover": {
                          backgroundColor: "#257885",
                          boxShadow: "0 4px 8px rgba(0,0,0,0.25)",
                        },
                        "&:active": {
                          backgroundColor: "#185059",
                          boxShadow: "0 1px 2px rgba(0,0,0,0.25)",
                        },
                      }}
                    >
                      Learn More About This Partnership
                    </Button>
                  </CardContent>
                </Card>

                {relatedPartners?.results &&
                  relatedPartners.results.length > 0 && (
                    <Card elevation={1} sx={{ borderRadius: "8px" }}>
                      <CardContent sx={{ p: 2.5 }}>
                        <Typography
                          variant="h6"
                          sx={{
                            fontFamily: "Montserrat, sans-serif",
                            fontSize: "1.125rem",
                            fontWeight: 400,
                            mb: 2,
                            color: "text.primary",
                          }}
                        >
                          RELATED PARTNERS
                        </Typography>
                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                          {relatedPartners.results
                            .slice(0, 5)
                            .map((relatedPartner, index) => (
                              <Box key={relatedPartner.id}>
                                <Link
                                  component="button"
                                  onClick={() =>
                                    navigate(`/partners/${relatedPartner.slug}`)
                                  }
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    width: "100%",
                                    py: 1.5,
                                    textDecoration: "none",
                                    color: "inherit",
                                    cursor: "pointer",
                                    background: "none",
                                    border: "none",
                                    textAlign: "left",
                                    fontFamily: "Montserrat, sans-serif",
                                    fontSize: "0.875rem",
                                    "&:hover": {
                                      "& .partner-name": {
                                        fontWeight: 700,
                                        color: "primary.main",
                                      },
                                      "& .arrow-icon": {
                                        color: "primary.main",
                                      },
                                    },
                                  }}
                                >
                                  <Box>
                                    <Typography
                                      className="partner-name"
                                      sx={{
                                        fontFamily: "Montserrat, sans-serif",
                                        fontSize: "0.875rem",
                                        fontWeight: 400,
                                        transition:
                                          "font-weight 0.2s, color 0.2s",
                                      }}
                                    >
                                      {relatedPartner.name}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontFamily: "Montserrat, sans-serif",
                                        color: "text.secondary",
                                        fontSize: "0.875rem",
                                      }}
                                    >
                                      {relatedPartner.industry_type_display}
                                    </Typography>
                                  </Box>
                                  <ChevronRight
                                    className="arrow-icon"
                                    sx={{
                                      fontSize: 20,
                                      color: "text.disabled",
                                      transition: "color 0.2s",
                                    }}
                                  />
                                </Link>
                                {index < relatedPartners.results.length - 1 && (
                                  <Divider />
                                )}
                              </Box>
                            ))}
                        </Box>
                      </CardContent>
                    </Card>
                  )}
              </Box>
            </Grid>
          </Grid>
        </Box>

        <PartnershipInquiryModal
          open={isInquiryModalOpen}
          onClose={() => setIsInquiryModalOpen(false)}
          partners={allPartners}
          preselectedPartnerSlug={partner?.slug}
        />
      </Box>
    </Layout>
  );
}
